import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AllCVOptionsFromDB, SkillWithCategory } from '../models/cv-options.model';
import { NewRegistrationSkills } from '../models/new-registration.model';
import { getSkills } from '../i18n/skills';

/**
 * Complete skills management component
 * Handles: list display, add new, edit, delete
 */
@Component({
  selector: 'app-skills-manager',
  templateUrl: './skills-manager.component.html',
  styleUrls: ['./skills-manager.component.scss'],
  standalone: false,
})
export class SkillsManagerComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() allCVOptionsFromDB!: AllCVOptionsFromDB;
  @Input() skills: NewRegistrationSkills[] = [];
  @Input() formGroupName?: string;
  @Input() controlName: string = 'otherSkills';
  @Input() testIdPrefix: string = 'skills';
  @Input() candidate: boolean = false;

  @Output() skillsChange = new EventEmitter<NewRegistrationSkills[]>();

  tempNewSkill = false;
  tempEditSkill = false;
  editingSkillCategory?: string;

  // Category management for skill selector
  skillsByCategory: Map<string, SkillWithCategory[]> = new Map();
  filteredSkillsByCategory: Map<string, SkillWithCategory[]> = new Map();
  expandedCategories: Set<string> = new Set();
  filteredCategories: string[] = [];

  constructor(private translate: TranslateService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.groupSkillsByCategory();
  }

  /**
   * Get the form control path based on whether formGroupName is provided
   */
  private getControlPath(): string {
    return this.formGroupName ? `${this.formGroupName}.${this.controlName}` : this.controlName;
  }

  /**
   * Groups skills by category
   */
  private groupSkillsByCategory(): void {
    if (!this.allCVOptionsFromDB?.skills) {
      return;
    }

    this.skillsByCategory.clear();
    this.allCVOptionsFromDB.skills.forEach((skill) => {
      const category = skill.category || 'other';
      if (!this.skillsByCategory.has(category)) {
        this.skillsByCategory.set(category, []);
      }
      this.skillsByCategory.get(category)!.push(skill);
    });

    this.filteredSkillsByCategory = new Map(this.skillsByCategory);
    this.filteredCategories = Array.from(this.skillsByCategory.keys()).sort();
  }

  /**
   * Get category display name
   */
  getCategoryDisplayName(categoryKey: string): string {
    return this.translate.instant('skillCategories.' + categoryKey);
  }

  /**
   * Open add new skill form
   */
  createNewSkill(): void {
    if (!this.tempEditSkill) {
      const controlPath = this.getControlPath();
      const skillsGroup = this.form.get(controlPath);
      skillsGroup?.reset();
      this.tempNewSkill = true;
      const addNewSkill = !this.form.get(`${controlPath}.addNewSkill`)?.value;
      this.form.get(`${controlPath}.addNewSkill`)?.setValue(addNewSkill);
    }
  }

  /**
   * Save new skill
   */
  saveNewSkill(): void {
    const controlPath = this.getControlPath();
    const skillsGroup = this.form.get(controlPath);
    const newSkill = new NewRegistrationSkills();
    const skillName = skillsGroup?.get('skillName')?.value;
    newSkill.skillName = skillName;
    newSkill.level = skillsGroup?.get('level')?.value;
    newSkill.edit = false;

    // Find the category for this skill
    const skillData = this.allCVOptionsFromDB?.skills?.find((s) => s.skillKey === skillName);
    if (skillData) {
      newSkill.category = skillData.category;
    }

    // Check for duplicate
    const isDuplicate = this.skills.some((skill) => skill.skillName === newSkill.skillName);

    if (isDuplicate) {
      this.translate.get('dashboard.createNewJobOffer.duplicateSkillError').subscribe((msg: string) => {
        this.snackBar.open(msg, this.translate.instant('shared.cancel'), {
          duration: 3000,
          panelClass: ['snackbar-formater'],
        });
      });
      return;
    }

    this.skills.push(newSkill);
    this.tempNewSkill = false;
    this.skillsChange.emit(this.skills);
  }

  /**
   * Close add new skill form
   */
  closeCreateNewSkill(): void {
    this.tempNewSkill = false;
    const controlPath = this.getControlPath();
    const skillsGroup = this.form.get(controlPath);

    skillsGroup?.get('skillName')?.clearValidators();
    skillsGroup?.get('skillName')?.reset();
    skillsGroup?.get('skillName')?.updateValueAndValidity();

    skillsGroup?.get('level')?.clearValidators();
    skillsGroup?.get('level')?.reset();
    skillsGroup?.get('level')?.updateValueAndValidity();

    skillsGroup?.get('addNewSkill')?.reset();
    skillsGroup?.get('edit')?.reset();
  }

  /**
   * Edit existing skill
   */
  editSkill(index: number): void {
    if (index > -1) {
      this.skills[index].edit = true;
      this.tempEditSkill = true;
      const controlPath = this.getControlPath();
      const skillsGroup = this.form.get(controlPath);
      const editNewSkills = !skillsGroup?.get('edit')?.value;
      skillsGroup?.get('edit')?.setValue(editNewSkills);
      skillsGroup?.get('skillName')?.setValue(this.skills[index].skillName);
      skillsGroup?.get('level')?.setValue(this.skills[index].level);

      // Set category to expand in the skill selector
      const skillData = this.allCVOptionsFromDB?.skills?.find((s) => s.skillKey === this.skills[index].skillName);
      if (skillData?.category) {
        this.editingSkillCategory = skillData.category;
      }
    }
  }

  /**
   * Save edited skill
   */
  saveEditSkill(index: number): void {
    if (index > -1) {
      const controlPath = this.getControlPath();
      const skillsGroup = this.form.get(controlPath);
      const updatedSkillName = skillsGroup?.get('skillName')?.value;
      const updatedLevel = skillsGroup?.get('level')?.value;

      // Check for duplicate
      const isDuplicate = this.skills.some((skill, i) => i !== index && skill.skillName === updatedSkillName);

      if (isDuplicate) {
        this.translate.get('dashboard.createNewJobOffer.duplicateSkillError').subscribe((msg: string) => {
          this.snackBar.open(msg, this.translate.instant('shared.cancel'), {
            duration: 3000,
            panelClass: ['snackbar-formater'],
          });
        });
        return;
      }

      this.skills[index].skillName = updatedSkillName;
      this.skills[index].level = updatedLevel;

      // Update the category when skill is changed
      const skillData = this.allCVOptionsFromDB?.skills?.find((s) => s.skillKey === updatedSkillName);
      if (skillData) {
        this.skills[index].category = skillData.category;
      }

      this.skills[index].edit = false;
      skillsGroup?.get('edit')?.setValue(false);
      this.tempEditSkill = false;
      this.skillsChange.emit(this.skills);
    }
  }

  /**
   * Close edit skill form
   */
  closeEditSkill(index: number): void {
    this.skills[index].edit = false;
    this.tempEditSkill = false;
    this.editingSkillCategory = undefined;
  }

  /**
   * Delete skill
   */
  deleteSkill(index: number): void {
    if (index > -1) {
      this.skills.splice(index, 1);
      this.tempEditSkill = false;
      this.skillsChange.emit(this.skills);
    }
  }
}
